import { type CSSProperties, useId, useState } from "react";
import { useUiStore } from "@/application/stores/uiStore";
import { Button } from "@/presentation/components/Button";
import { Icon } from "@/presentation/components/icons/Icon";
import { useOverlay } from "@/presentation/components/overlay/OverlayProvider";
import { Popover } from "@/presentation/components/overlay/Popover";
import { ProgressResetModal } from "./ProgressResetModal";
import "./SettingsPanel.css";

const NARRATION_SPEED_OPTIONS = [0.5, 1, 1.5, 2] as const;

/**
 * 帳（設定）ポップオーバー（O-1。ui-design.md §7.7 / screen-flow.md §2.3）。
 * モーダルにしない＝稽古を止めない。帳場の歯車から開く想定。
 *
 * 統合方法（integrator向けメモ）:
 * このコンポーネントはPopoverの開閉を自分自身で`useOverlay()`から読み書きするため、
 * 呼び出し側（画面ヘッダーの歯車ボタン）がすべきことは以下の2つだけ:
 *   1. `<SettingsPanel />`をヘッダー付近（歯車ボタンの隣）に一度マウントしておく
 *      （`open`が`false`の間は`Popover`が`null`を返すため常時マウントして問題ない）
 *   2. 歯車ボタンの`onClick`で`useOverlay().openPopover()`を呼ぶ
 * 例:
 *   const { openPopover } = useOverlay();
 *   <button onClick={openPopover}><Icon name="gear" decorative /></button>
 *   <SettingsPanel />
 *
 * O-1↔O-2の合成（このコンポーネント内で完結させる設計を選んだ）:
 * 進捗リセット確認（O-2 = `ProgressResetModal`）はこのコンポーネントがローカルstateで
 * 開閉を管理し、直接レンダーする（`LevelScreen`の中断確認モーダルと同じ「ローカルstate直書き」
 * の流儀に合わせた）。「記録の消去」押下時はリセットモーダルを開くと同時に明示的に
 * `closePopover()`も呼び、オーバーレイの同時表示ルール（モーダルが開いたらポップオーバーは
 * 閉じる。screen-flow.md §9.2、`OverlayProvider`が本来担保する規則）を確実に満たす。
 */
export function SettingsPanel() {
  const { popoverOpen, closePopover } = useOverlay();
  const [resetOpen, setResetOpen] = useState(false);

  const volume = useUiStore((state) => state.volume);
  const setVolume = useUiStore((state) => state.setVolume);
  const muted = useUiStore((state) => state.muted);
  const setMuted = useUiStore((state) => state.setMuted);
  const narrationSpeedMultiplier = useUiStore((state) => state.narrationSpeedMultiplier);
  const setNarrationSpeedMultiplier = useUiStore((state) => state.setNarrationSpeedMultiplier);
  const reduceMotionOverride = useUiStore((state) => state.reduceMotionOverride);
  const setReduceMotionOverride = useUiStore((state) => state.setReduceMotionOverride);

  const volumeId = useId();
  const mutedId = useId();
  const narrationSpeedId = useId();
  const reduceMotionId = useId();

  function handleOpenReset() {
    setResetOpen(true);
    closePopover();
  }

  return (
    <>
      <Popover open={popoverOpen} onClose={closePopover}>
        <div className="settings-panel">
          <div className="settings-panel__header">
            <h2 className="settings-panel__title">帳</h2>
            <Button variant="quiet" className="settings-panel__close-btn" onClick={closePopover}>
              閉じる
            </Button>
          </div>

          <div className="settings-panel__field">
            <label htmlFor={volumeId}>音量</label>
            <input
              id={volumeId}
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={(event) => setVolume(Number(event.target.value))}
              style={{ "--range-progress": `${volume * 100}%` } as CSSProperties}
            />
          </div>

          <div className="settings-panel__field settings-panel__field--row">
            <label htmlFor={mutedId}>
              <Icon name="mute" decorative /> 囃子・声を止める
            </label>
            <input
              id={mutedId}
              type="checkbox"
              checked={muted}
              onChange={(event) => setMuted(event.target.checked)}
            />
          </div>

          <div className="settings-panel__field">
            <label htmlFor={narrationSpeedId}>語りの速さ</label>
            <select
              id={narrationSpeedId}
              value={narrationSpeedMultiplier}
              onChange={(event) => setNarrationSpeedMultiplier(Number(event.target.value))}
            >
              {NARRATION_SPEED_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}×
                </option>
              ))}
            </select>
          </div>

          <div className="settings-panel__field settings-panel__field--row">
            <label htmlFor={reduceMotionId}>動きを抑える</label>
            <input
              id={reduceMotionId}
              type="checkbox"
              checked={reduceMotionOverride}
              onChange={(event) => setReduceMotionOverride(event.target.checked)}
            />
          </div>

          <Button variant="quiet" className="settings-panel__reset-btn" onClick={handleOpenReset}>
            <Icon name="reset" decorative /> 記録の消去
          </Button>
        </div>
      </Popover>
      <ProgressResetModal open={resetOpen} onClose={() => setResetOpen(false)} />
    </>
  );
}
